import { Tag } from "@/store/tags/tags-slice";
import { ActionTextField, FilterMenuOptionType } from "@faro-lotv/flat-ui";
import { useMemo, useState } from "react";
import { isTagWritable } from "./tag-accessibility";
import { validateNewTagName } from "./tag-utils";
import { useDeleteTag } from "./use-delete-tag";
import { useUpdateTag } from "./use-update-tag";

type UseElementTagOptionsProps = {
  /** The tags list to display */
  tags: Tag[];

  /** Whether dark styles should be used. */
  dark?: boolean;

  /** Whether the options should be disabled for interactions */
  disabled?: boolean;

  /** Callback called after a tag is deleted. Make sure to remove all references to the tag in cached state. */
  onTagRemoved?(tag: Tag): void;
};

export type UseElementTagOptionsReturn = {
  /** The options for the annotation tag filter menu */
  tagOptions: FilterMenuOptionType[];

  /** True if the tag is being processed (updating or deleting) */
  isProcessing: boolean;
};

/**
 * Hook used to get the options for tags to assign to an element.
 * It will handle the update and delete of tags, and will return if the project is being processed.
 *
 * @returns The options for the tags filter menu and the processing state
 */
export function useElementTagOptions({
  tags,
  dark,
  disabled = false,
  onTagRemoved: onDeleteButtonClick,
}: UseElementTagOptionsProps): UseElementTagOptionsReturn {
  const [isProcessing, setIsProcessing] = useState(false);
  const updateTag = useUpdateTag(setIsProcessing);
  const deleteTag = useDeleteTag(setIsProcessing);

  const tagOptions = useMemo(
    () =>
      tags
        .map((tag) => ({
          key: tag.id,
          value: tag.name,
          // Don't show a tooltip for the option, since the tooltip is already handled by the TextField component
          tooltip: "",
          label: (
            <ActionTextField
              inputText={tag.name}
              dark={dark}
              isEditable={isTagWritable(tag)}
              disabled={disabled || isProcessing}
              disableGutters
              fullWidth
              validate={(newName) => validateNewTagName(tag.id, newName, tags)}
              onConfirmButtonClick={(newName) => {
                updateTag(tag, newName);
              }}
              onDeleteButtonClick={() => {
                deleteTag(tag);
                onDeleteButtonClick?.(tag);
              }}
            />
          ),
        }))
        .sort((a, b) => a.value.localeCompare(b.value)),
    [
      dark,
      deleteTag,
      disabled,
      isProcessing,
      onDeleteButtonClick,
      tags,
      updateTag,
    ],
  );

  return { tagOptions, isProcessing };
}
